import {
  ClientUpdateJobPostDataDocument,
  CreativeUpdateJobPostDataDocument,
  JobPostingLocationType,
  UpdateJobPostDataFragment,
} from "@/gql/graphql"
import { POST_A_JOB_MODAL_DEFAULT_VALUES } from "./constants"
import { PartialPostAJobModalFormValues } from "./types"
import type { NovaSession } from "@/types"
import { useLazyQuery } from "@apollo/client"
import { useMemo } from "react"
import { formatCurrencyLocale } from "@/utils/formatCurrency"
import adjustDateForTimezone from "@/utils/dates"

const createUpdateData: (jobPost: UpdateJobPostDataFragment) => PartialPostAJobModalFormValues = jobPost => {
  if (!jobPost) return {}

  return {
    jobPostingId: jobPost.id,
    position: {
      value: jobPost.position?.id ? jobPost.position?.id : "",
      display: jobPost.position?.name ? jobPost.position?.name : "",
    },
    workType: {
      value: jobPost.creativeWorkType?.id ?? "",
      display: jobPost.creativeWorkType?.name ?? "",
      indefiniteArticle: jobPost.creativeWorkType?.indefiniteArticle ?? "",
    },
    location: {
      name: jobPost.locationName || "",
      lat: jobPost.location?.lat || "",
      lon: jobPost.location?.lon || "",
    },
    referenceImage: {
      file: "",
      name: "",
      existingImageUrl: jobPost.coverImage?.thumbnailUrl645 || "",
      existingPosterId: jobPost.coverImage?.id || "",
    },
    remote: Boolean(jobPost.locationType === JobPostingLocationType.Telecommute),
    title: jobPost.title || "",
    skills:
      jobPost.skills?.edges && jobPost.skills?.edges?.length > 0
        ? jobPost.skills?.edges.map(edge => ({
            id: edge?.node?.id!,
            name: edge?.node?.name!,
          }))
        : [],
    description: jobPost.description || "",
    startDate: jobPost.startDate ? adjustDateForTimezone(jobPost.startDate) : undefined,
    endDate: jobPost.endDate ? adjustDateForTimezone(jobPost.endDate) : undefined,
    datesFlexible: Boolean(jobPost.datesAreFlexible),
    jobType: jobPost.employmentType || POST_A_JOB_MODAL_DEFAULT_VALUES.jobType,
    payType: jobPost.payType || POST_A_JOB_MODAL_DEFAULT_VALUES.payType,
    budgetCurrency: jobPost.budget?.currency ?? POST_A_JOB_MODAL_DEFAULT_VALUES.budgetCurrency,
    rateCurrency: jobPost.pay?.currency ?? POST_A_JOB_MODAL_DEFAULT_VALUES.rateCurrency,
    budgetAmount: {
      value:
        Number(jobPost.budget?.amount) ??
        Number(jobPost.pay?.amount) ??
        POST_A_JOB_MODAL_DEFAULT_VALUES.budgetAmount.value,
      display:
        formatCurrencyLocale(jobPost.budget?.amount) ??
        formatCurrencyLocale(jobPost.pay?.amount) ??
        POST_A_JOB_MODAL_DEFAULT_VALUES.budgetAmount.display,
    },
    budgetMaxAmount: {
      value:
        Number(jobPost.budgetMax?.amount) ??
        Number(jobPost.payMax?.amount) ??
        POST_A_JOB_MODAL_DEFAULT_VALUES.budgetMaxAmount.value,
      display:
        formatCurrencyLocale(jobPost.budgetMax?.amount) ??
        formatCurrencyLocale(jobPost.payMax?.amount) ??
        POST_A_JOB_MODAL_DEFAULT_VALUES.budgetMaxAmount.display,
    },
    payDisplay: jobPost.payDisplay || POST_A_JOB_MODAL_DEFAULT_VALUES.payDisplay,
    rateFrequency: jobPost.payRate,
  }
}

export const useJobUpdateData = ({ serverSession }: { serverSession: NovaSession }) => {
  const userIsCreative = serverSession.account === "creative"
  const [getUpdateData, { data: updateJobPostingData, loading: loadingUpdateData }] = useLazyQuery(
    userIsCreative ? CreativeUpdateJobPostDataDocument : ClientUpdateJobPostDataDocument
  )

  const updateData = useMemo(() => {
    // FIXME: Fix this type later
    // @ts-ignore
    return updateJobPostingData?.jobPost ? createUpdateData(updateJobPostingData?.jobPost) : null
  }, [updateJobPostingData])

  return {
    getUpdateData,
    updateData,
    loadingUpdateData,
  }
}
